import {StateCreator} from "zustand/vanilla"
import {
  CatalogueFeature,
  CatalogueFeatureUpdate,
  ProductCatalogue,
  UpdateCatalogueFeatureRequest,
  UpdateCatalogueFeatureResponse,
} from "@devplaninc/codeup"
import {
  CheckCatalogueExistsResponse,
  CreateCatalogueSectionRequest,
  GetCatalogueFeatureUpdatesResponse,
  GetCatalogueFeaturesResponse,
  MoveCatalogueFeaturesRequest,
  MutateProductCatalogueResponse,
  RenameCatalogueSectionRequest,
} from "@/pb/api/devplan/services/web/company"
import {
  catalogCheckAPI,
  catalogFeatureUpdatesAPI,
  catalogFeatureAPI,
  catalogFeaturesAPI,
  catalogMoveFeaturesAPI,
  catalogSectionAPI,
  catalogSectionRenameAPI,
} from "@/app/apiPaths"
import {jr} from "@/hooks/queries/response-handler"

export type UpdateCatalogueFeatureInput = Pick<
  UpdateCatalogueFeatureRequest,
  "title" | "description" | "userFlow" | "techDesign"
>

export type CatalogueStoreState = {
  catalogueFeatures: Record<number, CatalogueFeature[]>
  productCatalogues: Record<number, ProductCatalogue | undefined>
  featureUpdates: Record<string, CatalogueFeatureUpdate>
  featureUpdateHistory: Record<string, CatalogueFeatureUpdate[]>
  catalogueExists: Record<number, boolean>
  fetchCatalogueFeatures(companyId: number): Promise<void>
  fetchCatalogueFeatureUpdates(companyId: number, featureId: string): Promise<void>
  fetchCatalogueExists(companyId: number): Promise<void>
  updateCatalogueFeature(companyId: number, featureId: string, input: UpdateCatalogueFeatureInput): Promise<CatalogueFeature>
  deleteCatalogueFeature(companyId: number, featureId: string): Promise<void>
  createCatalogueSection(companyId: number, input: CreateCatalogueSectionRequest): Promise<ProductCatalogue | undefined>
  renameCatalogueSection(companyId: number, sectionIndex: number, input: RenameCatalogueSectionRequest): Promise<ProductCatalogue | undefined>
  deleteCatalogueSection(companyId: number, sectionIndex: number): Promise<ProductCatalogue | undefined>
  moveCatalogueFeatures(companyId: number, input: MoveCatalogueFeaturesRequest): Promise<ProductCatalogue | undefined>
}

function parseProductCatalogueJson(productCatalogueJson: string): ProductCatalogue | undefined {
  return productCatalogueJson
    ? ProductCatalogue.fromJSON(JSON.parse(productCatalogueJson))
    : undefined
}

export function featureUpdateHistoryKey(companyId: number, featureId: string): string {
  return `${companyId}:${featureId}`
}

export const createCatalogueSlice: StateCreator<
  CatalogueStoreState,
  [],
  [],
  CatalogueStoreState
> = ((set) => ({
  catalogueFeatures: {},
  productCatalogues: {},
  featureUpdates: {},
  featureUpdateHistory: {},
  catalogueExists: {},
  fetchCatalogueFeatures: async (companyId) => {
    const response = await fetch(catalogFeaturesAPI(companyId))
    const js = await jr(response)
    const {featuresJson, productCatalogueJson, featureUpdatesJson} = GetCatalogueFeaturesResponse.fromJSON(js)
    const features = featuresJson.map(f => CatalogueFeature.fromJSON(JSON.parse(f)))
    const productCatalogue = productCatalogueJson
      ? ProductCatalogue.fromJSON(JSON.parse(productCatalogueJson))
      : undefined
    const updatesMap: Record<string, CatalogueFeatureUpdate> = {}
    for (const json of featureUpdatesJson) {
      const update = CatalogueFeatureUpdate.fromJSON(JSON.parse(json))
      if (update.featureId) {
        updatesMap[update.featureId] = update
      }
    }
    set(s => ({
      ...s,
      catalogueFeatures: {...s.catalogueFeatures, [companyId]: features},
      productCatalogues: {...s.productCatalogues, [companyId]: productCatalogue},
      featureUpdates: {...s.featureUpdates, ...updatesMap},
    }))
  },
  fetchCatalogueFeatureUpdates: async (companyId, featureId) => {
    const response = await fetch(catalogFeatureUpdatesAPI(companyId, featureId))
    const js = await jr(response)
    const {featureUpdatesJson} = GetCatalogueFeatureUpdatesResponse.fromJSON(js)
    const updates = featureUpdatesJson
      .map(json => CatalogueFeatureUpdate.fromJSON(JSON.parse(json)))
      .sort((a, b) => {
        const at = a.timestamp?.getTime() ?? 0
        const bt = b.timestamp?.getTime() ?? 0
        return bt - at
      })
    set(s => ({
      ...s,
      featureUpdateHistory: {
        ...s.featureUpdateHistory,
        [featureUpdateHistoryKey(companyId, featureId)]: updates,
      },
    }))
  },
  fetchCatalogueExists: async (companyId) => {
    const response = await fetch(catalogCheckAPI(companyId))
    const js = await jr(response)
    const {hasCatalogue} = CheckCatalogueExistsResponse.fromJSON(js)
    set(s => ({
      ...s,
      catalogueExists: {...s.catalogueExists, [companyId]: hasCatalogue},
    }))
  },
  updateCatalogueFeature: async (companyId, featureId, input) => {
    const response = await fetch(catalogFeatureAPI(companyId, featureId), {
      method: "PUT",
      body: JSON.stringify(UpdateCatalogueFeatureRequest.toJSON(UpdateCatalogueFeatureRequest.create(input))),
    })
    const js = await jr(response)
    const {feature} = UpdateCatalogueFeatureResponse.fromJSON(js)
    if (!feature) throw new Error("Feature update response did not include a feature")
    set(s => ({
      ...s,
      catalogueFeatures: {
        ...s.catalogueFeatures,
        [companyId]: (s.catalogueFeatures[companyId] ?? []).map(existing =>
          existing.id === feature.id ? feature : existing
        ),
      },
    }))
    return feature
  },
  deleteCatalogueFeature: async (companyId, featureId) => {
    const response = await fetch(catalogFeatureAPI(companyId, featureId), {method: "DELETE"})
    const js = await jr(response)
    const {productCatalogueJson} = MutateProductCatalogueResponse.fromJSON(js)
    const productCatalogue = parseProductCatalogueJson(productCatalogueJson)
    set(s => {
      const next: CatalogueStoreState = {
        ...s,
        catalogueFeatures: {
          ...s.catalogueFeatures,
          [companyId]: (s.catalogueFeatures[companyId] ?? []).filter(feature => feature.id !== featureId),
        },
      }
      if (productCatalogue) {
        next.productCatalogues = {...s.productCatalogues, [companyId]: productCatalogue}
      }
      return next
    })
  },
  createCatalogueSection: async (companyId, input) => {
    const response = await fetch(catalogSectionAPI(companyId), {
      method: "POST",
      body: JSON.stringify(CreateCatalogueSectionRequest.toJSON(input)),
    })
    const js = await jr(response)
    const {productCatalogueJson} = MutateProductCatalogueResponse.fromJSON(js)
    const productCatalogue = parseProductCatalogueJson(productCatalogueJson)
    set(s => ({
      ...s,
      productCatalogues: {...s.productCatalogues, [companyId]: productCatalogue},
    }))
    return productCatalogue
  },
  renameCatalogueSection: async (companyId, sectionIndex, input) => {
    const response = await fetch(catalogSectionRenameAPI(companyId, sectionIndex), {
      method: "PUT",
      body: JSON.stringify(RenameCatalogueSectionRequest.toJSON(input)),
    })
    const js = await jr(response)
    const {productCatalogueJson} = MutateProductCatalogueResponse.fromJSON(js)
    const productCatalogue = parseProductCatalogueJson(productCatalogueJson)
    set(s => ({
      ...s,
      productCatalogues: {...s.productCatalogues, [companyId]: productCatalogue},
    }))
    return productCatalogue
  },
  deleteCatalogueSection: async (companyId, sectionIndex) => {
    const response = await fetch(catalogSectionRenameAPI(companyId, sectionIndex), {method: "DELETE"})
    const js = await jr(response)
    const {productCatalogueJson} = MutateProductCatalogueResponse.fromJSON(js)
    const productCatalogue = parseProductCatalogueJson(productCatalogueJson)
    set(s => ({
      ...s,
      productCatalogues: {...s.productCatalogues, [companyId]: productCatalogue},
    }))
    return productCatalogue
  },
  moveCatalogueFeatures: async (companyId, input) => {
    const response = await fetch(catalogMoveFeaturesAPI(companyId), {
      method: "POST",
      body: JSON.stringify(MoveCatalogueFeaturesRequest.toJSON(input)),
    })
    const js = await jr(response)
    const {productCatalogueJson} = MutateProductCatalogueResponse.fromJSON(js)
    const productCatalogue = parseProductCatalogueJson(productCatalogueJson)
    set(s => ({
      ...s,
      productCatalogues: {...s.productCatalogues, [companyId]: productCatalogue},
    }))
    return productCatalogue
  },
}))
